import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { Avatar, CreatorName, formatNum, formatDuration, Loading } from "../components/common";
import { TipModal } from "../components/TipModal";
import { VideoCard } from "./TV";
import { usePlayer } from "../context/PlayerContext";
import { Button } from "../components/ui/button";
import { Coins, ThumbsUp, UserPlus, Check, Share2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { ShareToVSModal } from "../components/ShareToVSModal";
import { ogVideoUrl } from "../lib/config";

export default function VideoPage() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [related, setRelated] = useState([]);
  const [tipOpen, setTipOpen] = useState(false);
  const [following, setFollowing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const vref = useRef(); const lastSave = useRef(0);
  const { pauseForMedia, resumeFromMedia } = usePlayer();

  useEffect(() => {
    setV(null);
    api.get(`/tv/video/${id}`).then(({ data }) => { setV(data); setFollowing(data.channel?.is_following); });
    api.get("/tv/videos").then(({ data }) => setRelated(data.filter((x) => x.id !== id).slice(0, 6)));
  }, [id]);

  const onTime = () => {
    const el = vref.current; if (!el) return;
    if (el.currentTime - lastSave.current > 10) { lastSave.current = el.currentTime; api.post("/tv/progress", { video_id: id, progress: el.currentTime, duration: el.duration || 0 }).catch(() => {}); }
  };
  const follow = async () => { try { await (following ? api.delete(`/follow/${v.channel.id}`) : api.post(`/follow/${v.channel.id}`)); setFollowing(!following); } catch { toast.error("Error"); } };

  if (!v) return <Loading />;
  return (
    <div className="px-4 md:px-6 py-5 grid lg:grid-cols-[1fr_340px] gap-6">
      <div>
        <div className="rounded-2xl overflow-hidden bg-black"><video ref={vref} src={mediaUrl(v.video_url)} poster={v.thumbnail_url} controls autoPlay onTimeUpdate={onTime} onPlay={pauseForMedia} onPause={resumeFromMedia} onEnded={resumeFromMedia} className="w-full aspect-video" data-testid="video-player" /></div>
        <h1 className="font-display font-bold text-xl mt-4">{v.title}</h1>
        <div className="text-sm text-muted-foreground mt-1">{formatNum(v.view_count)} views · {v.category}{v.duration > 0 ? ` · ${formatDuration(v.duration)}` : ""}</div>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <Link to={`/${v.channel?.username}`} className="flex items-center gap-3"><Avatar src={v.channel?.avatar_url} name={v.channel?.display_name} size={44} /><div><div className="font-semibold text-sm"><CreatorName user={v.channel} /></div><div className="text-xs text-muted-foreground">{formatNum(v.channel?.follower_count || 0)} followers</div></div></Link>
          <Button onClick={follow} className={`rounded-full h-9 ml-auto ${following ? "btn-gold text-black" : "btn-blue"}`} data-testid="video-follow-btn">{following ? <><Check size={15} className="mr-1" />Following</> : <><UserPlus size={15} className="mr-1" />Follow</>}</Button>
          <Button variant="outline" className="rounded-full h-9 border-white/15 bg-white/5"><ThumbsUp size={15} className="mr-1" />{formatNum(v.like_count)}</Button>
          <Button onClick={() => setShareOpen(true)} variant="outline" className="rounded-full h-9 border-white/15 bg-white/5" data-testid="video-share-btn"><Share2 size={15} className="mr-1" />Share</Button>
          <Button onClick={() => { navigator.clipboard?.writeText(ogVideoUrl(v.id)); toast.success("Shareable link copied — rich preview ready"); }} variant="outline" className="rounded-full h-9 border-white/15 bg-white/5" data-testid="video-copylink-btn"><Link2 size={15} className="mr-1" />Copy link</Button>
          <Button onClick={() => setTipOpen(true)} className="btn-gold rounded-full h-9" data-testid="video-tip-btn"><Coins size={15} className="mr-1" />Tip</Button>
        </div>
        <p className="surface rounded-xl p-4 mt-4 text-sm text-muted-foreground whitespace-pre-wrap">{v.description || "No description."}</p>
      </div>
      <div>
        <div className="font-display font-semibold mb-3">Related</div>
        <div className="space-y-3">{related.map((r) => <VideoCard key={r.id} v={r} />)}</div>
      </div>
      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={v.channel} refType="video" refId={v.id} />
      {shareOpen && <ShareToVSModal entity={{ kind: "video", id: v.id, title: v.title, cover: v.thumbnail_url, author: v.channel }} onClose={() => setShareOpen(false)} />}
    </div>
  );
}
